import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import "../assets/css/About.css";
import SkillsSection from "./SkillsSection";

const experiences = [
  {
    title: "Java Full Stack Developer",
    company: "Shopify, Ottawa, Ontario",
    duration: "Feb 2025 - Present",
    points: [
      "Developed scalable Java 17 microservices using Spring Boot, Spring Cloud, and REST APIs.",
      "Built modern UI components using React 18, TypeScript, Redux Toolkit, Material UI, and micro-frontend architecture.",
      "Implemented event-driven architecture using Kafka for real-time data processing workflows.",
      "Designed AWS Lambda and DynamoDB based serverless health-check modules.",
      "Integrated GraphQL APIs to unify multiple backend services and improve frontend performance.",
      "Optimized Oracle 19c and MongoDB queries improving application performance by 35–40%.",
      "Implemented Spring Security, OAuth2, and JWT authentication for secure enterprise applications.",
      "Managed CI/CD pipelines using Jenkins, Maven, Git, and automated deployment workflows.",
      "Created unit and integration tests using JUnit 5, Mockito, Jest, and Cypress.",
    ],
    year: "2025",
  },

  {
    title: "Full Stack Developer",
    company: "State Street, Bengaluru, India",
    duration: "Jan 2021 - Dec 2023",
    points: [
      "Developed backend services using Java 11, Spring Boot, Spring MVC, and REST APIs.",
      "Built and deployed microservices using Docker, Kubernetes, Azure AKS, and PCF.",
      "Developed React.js components and integrated frontend applications with backend services.",
      "Implemented Kafka producers and consumers for asynchronous event-driven processing.",
      "Worked with MongoDB, Cassandra, and PostgreSQL databases for scalable applications.",
      "Designed GraphQL schemas to reduce API over-fetching and improve performance.",
      "Developed Angular components and dashboards for internal business applications.",
      "Implemented Redis caching to improve API response times.",
      "Created Jenkins CI/CD pipelines for automated builds, testing, and deployments.",
      "Monitored production systems using Elasticsearch, Kibana, and CloudWatch.",
    ],
    year: "2023",
  },

  {
    title: "Software Developer",
    company: "Alamy Images India Private Limited",
    duration: "Aug 2019 - Jan 2021",
    points: [
      "Developed enterprise web applications using Java, J2EE, Spring, Hibernate, HTML, CSS, and JavaScript.",
      "Created REST APIs using Spring MVC and integrated backend services using JPA/Hibernate.",
      "Developed JSP and Servlet based modules for enterprise applications.",
      "Designed responsive UI components using React, Bootstrap, jQuery, and AJAX.",
      "Implemented Kafka producers and consumers for asynchronous processing.",
      "Worked with Oracle and DB2 databases including queries, stored procedures, and triggers.",
      "Integrated AWS services including API Gateway, Cognito, and OpenSearch.",
      "Implemented OAuth2 and Spring Security based authentication mechanisms.",
      "Created CI/CD workflows using Jenkins, AWS CodePipeline, CodeBuild, and CodeDeploy.",
      "Performed debugging, production support, testing, and code reviews.",
    ],
    year: "2019",
  },
];


const education = [
  {
    degree: "Post-Graduate Diploma in Web Development",
    institution: "Conestoga College, Cambridge, Ontario, Canada",
    year: "Jan 2024 - Apr 2025",
  },
  {
    degree: "Bachelor of Technology in Information Technology",
    institution: "University of Kerala, Kerala, India",
    year: "Jun 2015 - Jul 2019",
  },
];

const About = () => {
  const timelineRef = useRef();
  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ["start end", "end start"],
  });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ["10%", "100%"]);

  const timelineData = [
    ...experiences,
    ...education.map((e) => ({
      title: e.degree,
      company: e.institution,
      duration: e.year,
      points: [],
      year: e.year.match(/\d{4}/g)?.[0] || "",
    })),
  ].sort((a, b) => b.year.localeCompare(a.year));

  return (
    <div id="about" className="min-h-screen bg-gradient-to-br from-[#0f0c29] via-[#302b63] to-[#24243e] text-white px-6 md:px-20 py-16 relative overflow-hidden">
      <h2 className="text-4xl font-bold text-center mb-16 z-10 relative">About Me</h2>

      <div className="relative z-10 timeline-wrapper" ref={timelineRef}>
        {/* Scroll-drawn animated vertical line */}
        <motion.div
  style={{ height: lineHeight }}
  className=" timeline-line absolute   top-0 w-[3px] bg-gradient-to-b from-pink-500 via-purple-500 to-blue-500 animate-gradient-glow z-0 origin-top"
/>


        {timelineData.map((item, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            viewport={{ once: true }}
            className="flex gap-10 items-start mb-24 relative group"
          >
            {/* Left Year */}
            <div className="w-20 text-right text-2xl font-extrabold text-[#A1A1AA]">
              {item.year}
            </div>

            {/* Timeline Dot */}
            <div className="relative z-10 flex flex-col items-center">
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                transition={{ duration: 0.4 }}
                className="w-5 h-5 bg-black border-[4px] border-white rounded-full"
              />
            </div>

            {/* Right Content */}
            <div className="flex-1">
              <h4 className="text-xl md:text-2xl font-bold text-white">
                {item.title}
                <span className="block text-gray-300 text-lg font-medium">
                  | {item.company}
                </span>
              </h4>
              <p className="text-sm italic text-purple-300 mt-1 mb-3">
                {item.duration}
              </p>
              {item.points.length > 0 && (
                <ul className="list-disc list-inside space-y-2 text-gray-300 text-base">
                  {item.points.map((point, idx) => (
                    <li key={idx}>{point}</li>
                  ))}
                </ul>
              )}
            </div>
          </motion.div>
        ))}
      </div>
      <SkillsSection />
      
    </div>
  );
};

export default About;
